import React from 'react'

import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select';
import startCase from 'lodash/startCase'
import { useDispatch, useSelector } from 'react-redux'

import type { SelectChangeEvent } from '@mui/material/Select';
import type { RootState } from 'reduxStore'

import * as PoziUtils from 'api/utils'
import { printUIOpen, setPrintReport } from 'utils/Print/actions'

import { setLayerPanelsFeatureOpen } from '../actions';

const styles: { [key: string]: React.CSSProperties } = {
  formControl: {
    margin: 8,
    width: '250px',
  },
}

interface LayerReportPropSelectProps {
  titleid: string
  selectValues: string[]
  geojson: object
}

const LayerReportPropSelect: React.FC<LayerReportPropSelectProps> = ({
  titleid,
  selectValues,
  geojson
}) => {
  const dispatch = useDispatch()
  const layer = useSelector((state: RootState) => state.layers[titleid])
  const layerAttributes = layer?.attributes

  // If any required props are missing, do not render the component
  if (
    !selectValues ||
    selectValues.length === 0 ||
    !geojson ||
    !titleid ||
    titleid === '' ||
    !layer ||
    (!layer.selected && !layer.visible)
  ) {
    return null
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string

    if (value && value !== '') {
      dispatch(setPrintReport(titleid, value))
      // Close the report panel for the layer and open the print UI
      dispatch(setLayerPanelsFeatureOpen(titleid, ''))
      dispatch(printUIOpen())
    }
  }

  const menuItemArr = []
  const sortedArr = PoziUtils.removeExcludedPropertiesFromArray(selectValues.sort() as [string])

  for (const [index, value] of sortedArr.entries()) {
    const key = 'layers-report-select-options-' + titleid + '-' + index

    // Use field alias as label instead of value if provided in attributes
    const label = layerAttributes?.[value]?.alias || value

    const labelDisplay = startCase(label.toLowerCase())
    menuItemArr.push(
      <MenuItem key={key} value={value}>
        <em>{labelDisplay}</em>
      </MenuItem>
    )
  }

  const id = 'layers-report-select-options-' + titleid

  return (
    <FormControl style={styles.formControl}>
      <InputLabel
        shrink
        htmlFor={id}
      >
        Data Grouping...
      </InputLabel>
      <Select
        variant='standard'
        value=''
        onChange={handleChange}
        displayEmpty
        // Don't render the menu in a portal (outside of the containing element), as this can cause an infinite loop trying to size and position the control.
        // This is due to conflicting styles in nested components which can trigger never ending React re-renders (Maximum call stack size exceeded).
        MenuProps={{ disablePortal: true }}
        inputProps={{
          name: 'selectedAttribute',
          id
        }}
      >
        {menuItemArr}
      </Select>
    </FormControl>
  )
}

export default LayerReportPropSelect
